import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  TextField,
  Divider,
  IconButton,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import ImageUploadField from "../../components/ImageUploadField";

// ── Shared sub-components (same as createpage) ──────────────────
const SectionTitle = ({ title, subtitle }) => (
  <Box mb={3}>
    <Typography variant="h6" fontWeight={600}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" mt={0.5}>
        {subtitle}
      </Typography>
    )}
    <Divider sx={{ mt: 1.5 }} />
  </Box>
);

const Field = ({
  label,
  value,
  onChange,
  multiline = false,
  placeholder = "",
  hint = "",
}) => (
  <Box mb={2}>
    <TextField
      fullWidth
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      multiline={multiline}
      minRows={multiline ? 3 : undefined}
      placeholder={placeholder}
      helperText={hint}
      size="small"
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
    />
  </Box>
);

const ItemCard = ({ index, label, onDelete, children }) => (
  <Box
    mb={2}
    p={2}
    sx={{
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: "10px",
      background: "rgba(255,255,255,0.03)",
    }}
  >
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      mb={1.5}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label} {index + 1}
      </Typography>
      <IconButton size="small" onClick={onDelete} color="error">
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
    {children}
  </Box>
);

const AddItemBtn = ({ label, onClick }) => (
  <Button
    variant="outlined"
    startIcon={<AddIcon />}
    onClick={onClick}
    fullWidth
    sx={{
      borderStyle: "dashed",
      borderRadius: "8px",
      textTransform: "none",
      mt: 1,
      color: "text.secondary",
      "&:hover": {
        borderStyle: "dashed",
        color: "#3b82f6",
        borderColor: "#3b82f6",
      },
    }}
  >
    {label}
  </Button>
);

// ── Default form ─────────────────────────────────────────────────
const defaultForm = {
  title: "",
  slug: "",
  headline: "",
  subheadline: "",
  category: "",
  client: "",
  industry: "",
  timeline: "",
  services: "",
  challenge: "",
  approach: "",
  results: [{ value: "", detail: "", label: "", timeframe: "" }],
  techStack: "",
  testimonial: "",
  testimonialClient: "",
  testimonialRole: "",
  testimonialInitials: "",
  thumbnailUrl: "",
  siteUrl: "",
};

// ── Main Component ────────────────────────────────────────────────
const CreateCaseStudy = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { caseId } = useParams();
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!caseId) return;
    const fetch = async () => {
      try {
        const res = await axios.get(`${apiUrl}/case-studies/id/${caseId}`);
        const d = res.data.data;
        setForm({
          ...d,
          techStack: Array.isArray(d.techStack)
            ? d.techStack.join(", ")
            : d.techStack || "",
          results: d.results?.length ? d.results : defaultForm.results,
        });
      } catch (err) {
        console.error(err);
      }
    };
    fetch();
  }, [caseId]);

  const set = (field, value) => {
    if (field === "title" && !caseId) {
      const slug = value
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
      setForm((p) => ({ ...p, title: value, slug }));
    } else {
      setForm((p) => ({ ...p, [field]: value }));
    }
  };

  const updateResult = (i, field, value) => {
    const results = [...form.results];
    results[i] = { ...results[i], [field]: value };
    setForm((p) => ({ ...p, results }));
  };

  const addResult = () =>
    setForm((p) => ({
      ...p,
      results: [
        ...p.results,
        { value: "", detail: "", label: "", timeframe: "" },
      ],
    }));
  const removeResult = (i) => {
    if (form.results.length <= 1) return;
    setForm((p) => ({
      ...p,
      results: p.results.filter((_, idx) => idx !== i),
    }));
  };

  const handleSave = async (publish = false, redirect = true) => {
    try {
      setSaving(true);
      const payload = {
        ...form,
        isPublished: publish,
        techStack: form.techStack
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };
      if (caseId) {
        await axios.put(`${apiUrl}/case-studies/${caseId}`, payload);
      } else {
        await axios.post(`${apiUrl}/case-studies/create`, payload);
      }
      if (redirect) navigate("/case-studies");
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box m="1.5rem 2.5rem">
      {/* Top bar */}
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <IconButton onClick={() => navigate("/case-studies")} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {caseId ? "Edit Case Study" : "Create Case Study"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fill out the case study details
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "12px",
          p: 3,
        }}
      >
        {/* Basic Info */}
        <SectionTitle title="Basic Info" />
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <Field
            label="Title"
            value={form.title}
            onChange={(v) => set("title", v)}
            placeholder="Comforty E-Commerce"
          />
          <Field
            label="Slug"
            value={form.slug}
            onChange={(v) => set("slug", v)}
            hint="Auto-generated from title"
          />
        </Box>
        <Field
          label="Headline"
          value={form.headline}
          onChange={(v) => set("headline", v)}
          placeholder="Modern Furniture Platform for Comforty.pk"
        />
        <Field
          label="Subheadline"
          value={form.subheadline}
          onChange={(v) => set("subheadline", v)}
          multiline
          placeholder="A premium online furniture platform..."
        />
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <Field
            label="Category"
            value={form.category}
            onChange={(v) => set("category", v)}
            placeholder="E-Commerce Platform"
          />
          <Field
            label="Site URL"
            value={form.siteUrl}
            onChange={(v) => set("siteUrl", v)}
            placeholder="https://comforty.pk"
          />
        </Box>
        <ImageUploadField
          label="Thumbnail Image"
          value={form.thumbnailUrl}
          onChange={(v) => set("thumbnailUrl", v)}
        />

        {/* Client Info */}
        <SectionTitle
          title="Client Info"
          subtitle="Shown in the info bar on detail page"
        />
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <Field
            label="Client"
            value={form.client}
            onChange={(v) => set("client", v)}
            placeholder="Comforty.pk"
          />
          <Field
            label="Industry"
            value={form.industry}
            onChange={(v) => set("industry", v)}
            placeholder="Furniture & Home Decor"
          />
        </Box>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <Field
            label="Timeline"
            value={form.timeline}
            onChange={(v) => set("timeline", v)}
            placeholder="4 months"
          />
          <Field
            label="Services"
            value={form.services}
            onChange={(v) => set("services", v)}
            placeholder="Full-Stack Development, UI/UX Design"
          />
        </Box>

        {/* Challenge & Approach */}
        <SectionTitle title="Challenge & Approach" />
        <Field
          label="The Challenge"
          value={form.challenge}
          onChange={(v) => set("challenge", v)}
          multiline
          placeholder="Client needed a scalable e-commerce platform..."
        />
        <Field
          label="Our Approach"
          value={form.approach}
          onChange={(v) => set("approach", v)}
          multiline
          placeholder="We built a modern full-stack platform..."
        />

        {/* Results */}
        <SectionTitle
          title="Results"
          subtitle="4 stat boxes shown in the dark section"
        />
        {form.results.map((r, i) => (
          <ItemCard
            key={i}
            index={i}
            label="Result"
            onDelete={() => removeResult(i)}
          >
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Field
                label="Value"
                value={r.value}
                onChange={(v) => updateResult(i, "value", v)}
                placeholder="10k"
              />
              <Field
                label="Detail (suffix)"
                value={r.detail}
                onChange={(v) => updateResult(i, "detail", v)}
                placeholder="+"
              />
            </Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Field
                label="Label"
                value={r.label}
                onChange={(v) => updateResult(i, "label", v)}
                placeholder="Monthly Product Views"
              />
              <Field
                label="Timeframe"
                value={r.timeframe}
                onChange={(v) => updateResult(i, "timeframe", v)}
                placeholder="Across furniture collections"
              />
            </Box>
          </ItemCard>
        ))}
        <AddItemBtn label="Add Result" onClick={addResult} />

        {/* Tech Stack */}
        <SectionTitle title="Tech Stack" />
        <Field
          label="Tech Stack (comma separated)"
          value={form.techStack}
          onChange={(v) => set("techStack", v)}
          placeholder="MongoDB, Express.js, React.js, Node.js, Tailwind CSS"
        />

        {/* Testimonial */}
        <SectionTitle title="Testimonial" />
        <Field
          label="Quote"
          value={form.testimonial}
          onChange={(v) => set("testimonial", v)}
          multiline
          placeholder="The platform successfully transformed our online presence..."
        />
        <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
          <Field
            label="Client Name"
            value={form.testimonialClient}
            onChange={(v) => set("testimonialClient", v)}
            placeholder="Comforty.pk Team"
          />
          <Field
            label="Role"
            value={form.testimonialRole}
            onChange={(v) => set("testimonialRole", v)}
            placeholder="Furniture & Design Team"
          />
          <Field
            label="Initials"
            value={form.testimonialInitials}
            onChange={(v) => set("testimonialInitials", v)}
            placeholder="CP"
          />
        </Box>
      </Box>

      {/* Footer buttons */}
      <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
        <Button
          variant="outlined"
          onClick={() => handleSave(false, false)}
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={14} color="inherit" /> : null
          }
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            borderColor: "rgba(255,255,255,0.2)",
            color: "text.secondary",
            "&:hover": { borderColor: "#3b82f6", color: "#3b82f6" },
          }}
        >
          {saving ? "Saving..." : "Save Progress"}
        </Button>
        <Button
          variant="outlined"
          onClick={() => handleSave(false)}
          disabled={saving}
          sx={{ textTransform: "none", borderRadius: "8px", px: 3 }}
        >
          Save Draft
        </Button>
        <Button
          variant="contained"
          onClick={() => handleSave(true)}
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{
            background: "#2e7d32",
            "&:hover": { background: "#1b5e20" },
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
          }}
        >
          {saving ? "Saving..." : "Save & Publish ✓"}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateCaseStudy;
